import { useMemo } from "react";
import useSWR, { SWRResponse } from "swr";
import type { Reaction } from "@memento/types/reaction";
import type { CardIdentifier } from "@memento/types/card";
import {
  ensureReactionContinuousSortOrderGeneral,
  sortReactions,
} from "@memento/library/reaction";
import { useAuthenticatedFetcher, getLoadingStatus } from "src/libs/fetcher";
import {
  getCardReactionsEndpointUrl,
  getCardReactionsPublicEndpointUrl,
} from "src/libs/api-endpoint-lib";
import { mergeArrays } from "src/libs/array-lib";
import { updateReaction } from "src/libs/reaction-lib";
import { useAuth } from "./useAuth";

export type UseCardReactionsResult = {
  reactions: Reaction[];
  haveReactions: boolean;
  saveReactions: (reactions: Reaction[]) => Promise<void>;
};

export const useCardReactions = (cardIdentifier: CardIdentifier | null) => {
  const { getAccessToken } = useAuth();
  const fetcher = useAuthenticatedFetcher();
  const key = cardIdentifier
    ? getCardReactionsEndpointUrl(cardIdentifier)
    : null;

  const { data, error, mutate }: SWRResponse<Reaction[], Error> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  const result = useMemo<UseCardReactionsResult | null>(() => {
    if (!data) {
      return null;
    }

    // assuming these are already sorted from the API
    const reactions = data;

    const haveReactions = reactions.length > 0;

    async function saveReactions(mutatedReactions: Reaction[]) {
      const originalReactionsToUpdate = mutatedReactions;

      let allReactionSorted = sortReactions(
        mergeArrays(
          (r) => r.reactionId,
          originalReactionsToUpdate,
          reactions || []
        )
      );

      // call this first so that the change is more responsive to the user. will
      // call again later
      mutate(allReactionSorted, false);

      // these will be reactions that have their sortOrder updated
      const moreReactionsToUpdate: Reaction[] = [];

      // make sure sortOrders are continuous
      await ensureReactionContinuousSortOrderGeneral(
        allReactionSorted,
        async (reaction: Reaction) => {
          moreReactionsToUpdate.push(reaction);
        }
      );

      const haveMoreReactionsToUpdate = moreReactionsToUpdate.length > 0;

      let finalReactionsToUpdate = originalReactionsToUpdate;

      // if we have new reactions to update, then we need to update them for the
      // mutateReactions call
      if (haveMoreReactionsToUpdate) {
        // merge so that we don't end up with duplicates
        finalReactionsToUpdate = mergeArrays(
          (r) => r.reactionId,
          // moreReactionsToUpdate needs to come first since it has the latest sortOrder
          moreReactionsToUpdate,
          originalReactionsToUpdate
        );

        allReactionSorted = sortReactions(
          mergeArrays(
            (r) => r.reactionId,
            finalReactionsToUpdate,
            reactions || []
          )
        );

        // call this again so that the change is more responsive to the user
        mutate(allReactionSorted, false);
      }

      await Promise.all(
        finalReactionsToUpdate.map((reaction) => {
          return updateReaction(
            getAccessToken,
            { cardId: reaction.cardId, reactionId: reaction.reactionId },
            // only allowing a couple fields to be modified at this point
            {
              sortOrder: reaction.sortOrder,
              publishStatus: reaction.publishStatus,
            }
          );
        })
      );
    }

    return {
      reactions,
      haveReactions,
      saveReactions,
    };
  }, [mutate, data, getAccessToken]);

  return {
    result,
    error,
    status: getLoadingStatus(key, data, error),
    mutate,
  };
};

type PublicReactionsResult = {
  publishedReactions: Reaction[];
  pendingCount: number;
};

export const useCardReactionsPublic = (
  cardIdentifier: CardIdentifier | null
) => {
  const fetcher = useAuthenticatedFetcher();
  const key = cardIdentifier
    ? getCardReactionsPublicEndpointUrl(cardIdentifier)
    : null;

  const { data, error, mutate }: SWRResponse<PublicReactionsResult, Error> =
    useSWR(key, fetcher);

  return {
    publishedReactions: data?.publishedReactions || [],
    pendingCount: data?.pendingCount || 0,
    error,
    status: getLoadingStatus(key, data, error),
    mutate,
  };
};
